import { useLazyQuery } from "@apollo/client/react"
import { Autocomplete, TextField, Typography, Avatar } from "@mui/material"
import { debounce } from "lodash"
import React, { useState, useCallback, useMemo, useEffect } from "react"

import { GetCustomerContactsDocument } from "@/graphql/graphql"
import i18n from "@/i18n"

interface CustomerData {
  id?: string | null
  name: string
  email: string
  phone: string
}

interface CustomerOption {
  id: string
  name: string | null | undefined
  email: string | null | undefined
  phone: string | null | undefined
  avatarUrl: string | null | undefined
}

type SearchField = "name" | "email" | "phone"

interface CustomerAutocompleteProps {
  onChange: (data: CustomerData) => void
  onCustomerSelected?: (customerId: string | null) => void
  initialValues?: {
    name?: string
    email?: string
    phone?: string
  }
  touched?: {
    name?: boolean
    email?: boolean
    phone?: boolean
  }
  errors?: {
    name?: string
    email?: string
    phone?: string
  }
  disabled?: boolean
}

const CustomerAutocomplete: React.FC<CustomerAutocompleteProps> = ({
  onChange,
  onCustomerSelected,
  initialValues,
  touched = {},
  errors = {},
  disabled = false,
}) => {
  const [formData, setFormData] = useState<CustomerData>({
    name: initialValues?.name || "",
    email: initialValues?.email || "",
    phone: initialValues?.phone || "",
  })
  const [activeField, setActiveField] = useState<SearchField | null>(null)
  const [_searchTerm, setSearchTerm] = useState("")

  // Sync formData when initialValues change (e.g., switching reservations)
  useEffect(() => {
    setFormData({
      name: initialValues?.name || "",
      email: initialValues?.email || "",
      phone: initialValues?.phone || "",
    })
  }, [initialValues?.name, initialValues?.email, initialValues?.phone])

  const [fetchCustomers, { data, loading }] = useLazyQuery(GetCustomerContactsDocument)

  const customerContacts = (data?.business?.customerContacts?.collection || []) as any[]

  const options: CustomerOption[] = useMemo(() => {
    return customerContacts.map((contact) => ({
      id: contact.id,
      // NOTE: types will align after `yarn generate` updates GraphQL typings
      name: contact.name,
      email: contact.email,
      phone: contact.phoneFormatted,
      avatarUrl: contact.avatarUrl,
    }))
  }, [customerContacts])

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string, field: SearchField) => {
        if (value.length >= 2) {
          void fetchCustomers({
            variables: {
              search: value,
              searchField: field,
              perPage: 5,
            },
          })
          setSearchTerm(value)
        } else {
          setSearchTerm("")
        }
      }, 300),
    [fetchCustomers],
  )

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  const handleFieldChange = useCallback(
    (field: keyof CustomerData, searchFieldName: SearchField) => (value: string) => {
      const updatedData = { ...formData, [field]: value, id: null }
      setFormData(updatedData)
      onChange(updatedData)
      onCustomerSelected?.(null)
      debouncedSearch(value, searchFieldName)
    },
    [formData, onChange, onCustomerSelected, debouncedSearch],
  )

  const handleSelectCustomer = useCallback(
    (customer: CustomerOption | string | null) => {
      // Only handle actual customer object selections, ignore string values from freeSolo
      if (customer && typeof customer !== "string") {
        const updatedData: CustomerData = {
          id: customer.id,
          name: customer.name || "",
          email: customer.email || "",
          phone: customer.phone || "",
        }
        setFormData(updatedData)
        onChange(updatedData)
        onCustomerSelected?.(customer.id)
      }
      setActiveField(null)
      setSearchTerm("")
    },
    [onChange, onCustomerSelected],
  )

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: CustomerOption) => (
    <li {...props} key={option.id}>
      <div className="flex items-center gap-3">
        <Avatar src={option.avatarUrl || undefined} sx={{ width: 32, height: 32 }}>
          {option.name?.[0]}
        </Avatar>
        <div>
          <Typography variant="body1">{option.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {option.email} {option.phone ? `• ${option.phone}` : null}
          </Typography>
        </div>
      </div>
    </li>
  )

  const getOptionLabel = useCallback((option: CustomerOption | string, fieldType?: SearchField): string => {
    if (typeof option === "string") return option

    switch (fieldType) {
      case "name":
        return option.name || ""
      case "email":
        return option.email || ""
      case "phone":
        return option.phone || ""
      default:
        return option.name || ""
    }
  }, [])

  return (
    <div className="space-y-3" data-testid="panel-reservations-create-form-customer">
      <Autocomplete
        freeSolo
        disabled={disabled}
        options={activeField === "name" ? options : []}
        loading={activeField === "name" && loading}
        inputValue={formData.name}
        onInputChange={(_, value) => handleFieldChange("name", "name")(value)}
        onChange={(_, value) => handleSelectCustomer(value)}
        onFocus={() => setActiveField("name")}
        onBlur={() => setActiveField(null)}
        filterOptions={(x) => x}
        getOptionLabel={(option) => getOptionLabel(option, "name")}
        renderOption={renderOption}
        data-testid="panel-reservations-create-form-customer-name"
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            label={i18n.t("app.reservation.labels.name")}
            error={Boolean(touched.name && errors.name)}
            helperText={touched.name ? errors.name : null}
          />
        )}
      />
      <Autocomplete
        freeSolo
        disabled={disabled}
        options={activeField === "email" ? options : []}
        loading={activeField === "email" && loading}
        inputValue={formData.email}
        onInputChange={(_, value) => handleFieldChange("email", "email")(value)}
        onChange={(_, value) => handleSelectCustomer(value)}
        onFocus={() => setActiveField("email")}
        onBlur={() => setActiveField(null)}
        filterOptions={(x) => x}
        getOptionLabel={(option) => getOptionLabel(option, "email")}
        renderOption={renderOption}
        data-testid="panel-reservations-create-form-customer-email"
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            label={i18n.t("app.reservation.labels.email")}
            type="email"
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email ? errors.email : null}
          />
        )}
      />
      <Autocomplete
        freeSolo
        disabled={disabled}
        options={activeField === "phone" ? options : []}
        loading={activeField === "phone" && loading}
        inputValue={formData.phone}
        onInputChange={(_, value) => handleFieldChange("phone", "phone")(value)}
        onChange={(_, value) => handleSelectCustomer(value)}
        onFocus={() => setActiveField("phone")}
        onBlur={() => setActiveField(null)}
        filterOptions={(x) => x}
        getOptionLabel={(option) => getOptionLabel(option, "phone")}
        renderOption={renderOption}
        data-testid="panel-reservations-create-form-customer-phone"
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            label={i18n.t("app.reservation.labels.phone")}
            error={Boolean(touched.phone && errors.phone)}
            helperText={touched.phone ? errors.phone : null}
          />
        )}
      />
    </div>
  )
}

export default CustomerAutocomplete
